<template>
  <BarChart
    :chartData="chartData"
    :chartOptions="chartOptions"
    :plugins="chartPlugins"
    :styles="chartStyles"
    :chart-id="chartId"
  />
</template>

<script setup>
import { computed } from 'vue'
import { Chart as ChartJS, LineController, LineElement, PointElement } from 'chart.js'
import BarChart from '@/components/atom/chart/barChart'
import figureFormatter from '@/services/utils/figureFormatter'
import { ACTUAL_COLOR, BUDGET_BORDER_COLOR, BUDGET_COLOR, BUDGET_LINE_COLOR, LINE_MARK, axisScaleOf, formatAmount, formatAxisValue, formatPercent, isPeriodInRange, statusOf, usedPercent, varianceOf, variancePercent } from '@/components/molecule/company/dashboard/dynamic-dashboard/budget/budgetFigures'

ChartJS.register(LineController, LineElement, PointElement)

const HORIZONTAL = 'horizontal'
const TEXT_COLOR = '#667085'
const GRID_COLOR = '#eaecf0'
const BASELINE_COLOR = '#d0d5dd'
const ZERO_LINE_COLOR = '#98a2b3'
const TOOLTIP_COLOR = '#101828'
const STATUS_COLORS = { favourable: '#0f766e', adverse: '#d92d20', neutral: '#667085' }
const LABEL_SPACE = 84
const NARROW_LABEL_SPACE = 72
const NARROW_WIDTH = 420
const LABEL_LENGTH = 24
const NARROW_LABEL_LENGTH = 10
const HORIZONTAL_BUDGET_THICKNESS = 22
const HORIZONTAL_ACTUAL_THICKNESS = 10
const VERTICAL_BUDGET_THICKNESS = 44
const VERTICAL_ACTUAL_THICKNESS = 24
const MAX_LABEL_PERCENT = 999
const ROW_HEIGHT = 44
const AXIS_HEIGHT = 56
const MIN_HEIGHT = 180
const PERIOD_HEIGHT = 300

const props = defineProps({
  rows: { type: Array, required: true },
  direction: { type: String, required: true },
  favourableWhen: { type: String, required: true },
  budgetMark: { type: String, required: true },
  showUsed: { type: Boolean, default: false },
  budgetOnly: { type: Boolean, default: false },
  asOf: { type: String, default: '' },
  chartId: { type: String, required: true }
})

const { limitText } = figureFormatter()

const isHorizontal = props.direction === HORIZONTAL
const indexAxis = isHorizontal ? 'y' : 'x'
const valueAxis = isHorizontal ? 'x' : 'y'
const budgetThickness = isHorizontal ? HORIZONTAL_BUDGET_THICKNESS : VERTICAL_BUDGET_THICKNESS
const actualThickness = isHorizontal ? HORIZONTAL_ACTUAL_THICKNESS : VERTICAL_ACTUAL_THICKNESS

const budgetBar = () => ({
  backgroundColor: BUDGET_COLOR,
  borderColor: BUDGET_BORDER_COLOR,
  borderWidth: 1,
  barPercentage: 0.9,
  categoryPercentage: 0.85,
  maxBarThickness: budgetThickness,
  borderRadius: 4,
  grouped: false,
  order: 2
})

const budgetLine = () => ({
  type: LINE_MARK,
  borderColor: BUDGET_LINE_COLOR,
  backgroundColor: BUDGET_LINE_COLOR,
  borderWidth: 2,
  pointRadius: 3,
  order: 0
})

const chartData = computed(() => ({
  labels: props.rows.map(row => row.label),
  datasets: [
    {
      label: 'Budget',
      data: props.rows.map(row => row.budget),
      ...(props.budgetMark === LINE_MARK ? budgetLine() : budgetBar())
    },
    {
      label: 'Actual',
      data: props.rows.map(row => row.actual),
      backgroundColor: ACTUAL_COLOR,
      barPercentage: 0.5,
      categoryPercentage: 0.85,
      maxBarThickness: actualThickness,
      borderRadius: 4,
      grouped: false,
      order: 1
    }
  ]
}))

const axisScale = computed(() => {
  const largest = Math.max(0, ...props.rows.map(row => Math.max(Math.abs(row.budget), Math.abs(row.actual || 0))))
  return axisScaleOf(largest)
})

const chartStyles = computed(() => ({
  position: 'relative',
  height: `${isHorizontal ? Math.max(MIN_HEIGHT, props.rows.length * ROW_HEIGHT + AXIS_HEIGHT) : PERIOD_HEIGHT}px`
}))

const rowVariance = (row) => varianceOf(row.budget, row.actual || 0, props.favourableWhen)

const varianceText = (row) => {
  const variance = rowVariance(row)
  if (! variance) return statusOf(variance).name
  if (! row.budget) return 'No budget'
  const percent = Math.abs(variancePercent(variance, row.budget))
  return `${statusOf(variance).arrow} ${percent > MAX_LABEL_PERCENT ? `>${MAX_LABEL_PERCENT}%` : formatPercent(percent)}`
}

const varianceColor = (row) => {
  const variance = rowVariance(row)
  if (variance < 0) return STATUS_COLORS.adverse
  return variance > 0 ? STATUS_COLORS.favourable : STATUS_COLORS.neutral
}

const varianceLine = (row) => {
  const variance = rowVariance(row)
  const percent = row.budget ? ` · ${formatPercent(variancePercent(variance, row.budget))}` : ''
  return `Variance: ${formatAmount(variance)}${percent}`
}

const isPeriodRunning = (row) => {
  if (isHorizontal || ! props.asOf) return false
  return isPeriodInRange(row.label, props.asOf, props.asOf)
}

const tooltipLines = (row) => {
  if (props.budgetOnly) return [`Budget: ${formatAmount(row.budget)}`]
  if (row.actual === null) return [`Budget: ${formatAmount(row.budget)}`, 'Actual: Not started']
  const lines = [`Budget: ${formatAmount(row.budget)}`, `Actual: ${formatAmount(row.actual)}`, varianceLine(row), `Status: ${statusOf(rowVariance(row)).name}`]
  const usedLines = props.showUsed ? [...lines, `Used: ${formatPercent(usedPercent(row.budget, row.actual))}`] : lines
  return isPeriodRunning(row) ? [...usedLines, 'Period in progress'] : usedLines
}

const axisValue = (value) => formatAxisValue(value, axisScale.value)

const categoryLabel = function (value) {
  const row = props.rows[value]
  if (! row) return ''
  if (! isHorizontal) return row.label.split(' - ')
  return limitText(row.label, this.chart.width < NARROW_WIDTH ? NARROW_LABEL_LENGTH : LABEL_LENGTH)
}

const rightSpace = (width) => {
  if (! isHorizontal || props.budgetOnly) return 0
  return width < NARROW_WIDTH ? NARROW_LABEL_SPACE : LABEL_SPACE
}

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  indexAxis: indexAxis,
  layout: { padding: (context) => ({ right: rightSpace(context.chart.width) }) },
  interaction: { mode: 'index', axis: indexAxis, intersect: false },
  scales: {
    [indexAxis]: {
      grid: { display: false, borderColor: BASELINE_COLOR },
      ticks: { autoSkip: ! isHorizontal, maxRotation: 0, font: { size: 11 }, color: TEXT_COLOR, callback: categoryLabel }
    },
    [valueAxis]: {
      beginAtZero: true,
      grid: { color: GRID_COLOR, borderDash: [3, 3], drawBorder: false, drawTicks: false },
      ticks: { padding: 8, font: { size: 11 }, color: TEXT_COLOR, callback: axisValue }
    }
  },
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: TOOLTIP_COLOR,
      cornerRadius: 8,
      padding: 12,
      displayColors: false,
      callbacks: {
        title: (items) => (items.length ? props.rows[items[0].dataIndex].label : ''),
        label: () => '',
        afterBody: (items) => (items.length ? tooltipLines(props.rows[items[0].dataIndex]) : [])
      }
    }
  }
}

const varianceLabels = {
  id: 'budgetVarianceLabels',
  afterDatasetsDraw: (chart) => {
    if (props.budgetOnly) return
    const points = chart.getDatasetMeta(0).data
    const ctx = chart.ctx
    ctx.save()
    ctx.textAlign = 'left'
    ctx.textBaseline = 'middle'
    ctx.font = '600 11px sans-serif'
    points.forEach((point, index) => {
      const row = props.rows[index]
      if (! row) return
      ctx.fillStyle = varianceColor(row)
      ctx.fillText(varianceText(row), chart.chartArea.right + 8, point.y)
    })
    ctx.restore()
  }
}

const zeroLine = {
  id: 'budgetZeroLine',
  beforeDatasetsDraw: (chart) => {
    const scale = chart.scales[valueAxis]
    if (scale.min >= 0) return
    const position = scale.getPixelForValue(0)
    const area = chart.chartArea
    const ctx = chart.ctx
    ctx.save()
    ctx.strokeStyle = ZERO_LINE_COLOR
    ctx.beginPath()
    ctx.moveTo(isHorizontal ? position : area.left, isHorizontal ? area.top : position)
    ctx.lineTo(isHorizontal ? position : area.right, isHorizontal ? area.bottom : position)
    ctx.stroke()
    ctx.restore()
  }
}

const chartPlugins = isHorizontal ? [varianceLabels, zeroLine] : [zeroLine]
</script>
